import { useEffect, useState } from "react";
import { Check, CheckCircle2, MessageCircle, Plus, Search, Trash2, Undo2 } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast, Toaster } from "sonner";
import { Reveal, Floral, GoldDivider } from "../components/Decor";
import { ASSESSORIA, RSVP } from "../constants/site";

function maskPhone(v) {
  const d = (v || "").replace(/\D/g, "").slice(0, 11);
  if (d.length === 0) return "";
  if (d.length <= 2) return `(${d}`;
  if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
}

const validPhone = (v) => {
  const d = (v || "").replace(/\D/g, "");
  return d.length === 10 || d.length === 11;
};

let slotSeq = 0;
const newSlot = (name = "", categoria = "adulto") => ({ key: `comp-${Date.now()}-${slotSeq++}`, name, categoria });

const MAX_COMPANIONS = 10;

const FaixasEtarias = () => (
  <div className="mx-auto mt-10 max-w-2xl px-5" data-testid="rsvp-faixas-etarias">
    <div className="border border-gold-border bg-paperdeep px-6 py-4 text-center">
      <p className="font-sans text-[11px] font-semibold uppercase tracking-[0.24em] text-sage-dark">
        Atenção às faixas etárias
      </p>
      <div className="mt-2 space-y-1 font-sans text-sm font-light leading-relaxed text-ink/80">
        <p>
          <strong>Adulto:</strong> a partir de 12 anos.
        </p>
        <p>
          <strong>Criança:</strong> de 05 anos a 11 anos.
        </p>
        <p>
          <strong>Bebê:</strong> até 04 anos.
        </p>
      </div>
    </div>
  </div>
);

export default function ConfirmacaoPresenca() {
  const [status, setStatus] = useState(null);
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [searching, setSearching] = useState(false);
  const [invite, setInvite] = useState(null);
  const [existing, setExisting] = useState(null);
  const [titularVai, setTitularVai] = useState(true);
  const [companions, setCompanions] = useState([]);
  const [phone, setPhone] = useState("");
  const [phoneOwner, setPhoneOwner] = useState("");
  const [mensagem, setMensagem] = useState("");
  const [sent, setSent] = useState(null);
  const [busy, setBusy] = useState(false);
  const [loadError, setLoadError] = useState(false);

  const loadStatus = () => {
    setLoadError(false);
    api
      .get("/rsvp/status")
      .then(({ data }) => setStatus(data))
      .catch(() => {
        setStatus(null);
        setLoadError(true);
      });
  };

  useEffect(() => {
    loadStatus();
  }, []);

  useEffect(() => {
    if (invite || sent) return;
    const t = setTimeout(async () => {
      if (query.trim().length < 2) {
        setSuggestions([]);
        return;
      }
      setSearching(true);
      try {
        const { data } = await api.get("/rsvp/search", { params: { q: query } });
        setSuggestions(data.suggestions || []);
      } catch {
        setSuggestions([]);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [query, invite, sent]);

  const selectInvite = async (g) => {
    try {
      const { data } = await api.get(`/rsvp/invite/${g.id}`);
      const r = data.rsvp;
      if (r && r.status === "confirmado") {
        setTitularVai(true);
        setCompanions((r.presentes || []).filter((p) => !p.titular).map((p) => newSlot(p.name, p.categoria)));
        setMensagem(r.mensagem || "");
      } else if (r && r.status === "ausente") {
        setTitularVai(false);
        setCompanions([]);
        setMensagem(r.mensagem || "");
      } else {
        setTitularVai(true);
        setCompanions([]);
        setMensagem("");
      }
      setPhone(r?.telefone || "");
      setPhoneOwner(r?.telefone_pessoa || "");
      setExisting(r || null);
      setInvite(data.invite);
      setQuery("");
      setSuggestions([]);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const updateSlot = (key, patch) =>
    setCompanions((cs) => cs.map((c) => (c.key === key ? { ...c, ...patch } : c)));

  const removeSlot = (key) => setCompanions((cs) => cs.filter((c) => c.key !== key));

  const addCompanion = () => {
    if (companions.length >= MAX_COMPANIONS) return;
    setCompanions((cs) => [...cs, newSlot()]);
  };

  const submit = async () => {
    if (busy) return;
    if (!validPhone(phone)) {
      toast.error("Informe um telefone válido com DDD.");
      return;
    }
    if (titularVai) {
      for (const c of companions) {
        if (c.name.trim().split(/\s+/).filter(Boolean).length < 2) {
          toast.error("Informe o nome completo (nome e sobrenome) de todos os participantes.");
          return;
        }
      }
    }
    setBusy(true);
    try {
      const presentes = titularVai
        ? [
            { name: invite.name, categoria: "adulto", titular: true },
            ...companions.map((c) => ({ name: c.name.trim(), categoria: c.categoria, titular: false })),
          ]
        : [];
      const { data } = await api.post("/rsvp/confirm", {
        guest_id: invite.id,
        ausente: !titularVai,
        telefone: phone,
        telefone_pessoa: phoneOwner,
        mensagem,
        presentes,
      });
      setSent({ status: data.status, presentes, guestName: invite.name, telefone: phone });
    } catch (e) {
      if (e.response?.status === 403) setStatus({ open: false, deadline: "03/12/2026" });
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const reset = () => {
    setSent(null);
    setInvite(null);
    setExisting(null);
    setCompanions([]);
    setPhone("");
    setPhoneOwner("");
    setMensagem("");
  };

  return (
    <div className="relative min-h-screen overflow-hidden pt-28" data-testid="page-rsvp">
      <Floral variant="top" mirrored className="absolute -right-24 -top-6 w-80 opacity-85" />
      <header className="relative mx-auto max-w-3xl px-5 text-center">
        <p className="eyebrow">Casamento de Talita &amp; Rodrigo</p>
        <h1 className="mt-4 font-script text-6xl text-ink sm:text-7xl">{RSVP.title}</h1>
        <GoldDivider className="mt-8" />
        <p className="mx-auto mt-6 max-w-xl font-serif text-xl italic text-ink/80">{RSVP.subtitle}</p>
        <p className="mt-4 inline-block border border-gold-border bg-white px-5 py-2 font-sans text-[11px] uppercase tracking-[0.22em] text-sage-dark" data-testid="rsvp-deadline">
          Confirme sua presença até {RSVP.deadlineLabel}
        </p>
      </header>

      {status?.open && <FaixasEtarias />}

      <section className="mx-auto max-w-2xl px-5 py-12">
        {status === null ? (
          loadError ? (
            <Reveal>
              <div className="paper-card gold-frame p-10 text-center" data-testid="rsvp-load-error">
                <p className="font-serif text-2xl italic text-ink">Não foi possível carregar a confirmação agora.</p>
                <p className="mt-3 font-sans text-sm font-light leading-loose text-muted">
                  Verifique sua conexão com a internet e tente novamente.
                </p>
                <button onClick={loadStatus} data-testid="rsvp-retry-btn" className="btn-outline mt-6">
                  Tentar novamente
                </button>
              </div>
            </Reveal>
          ) : null
        ) : !status.open ? (
          <Reveal>
            <div className="paper-card gold-frame p-10 text-center" data-testid="rsvp-closed">
              <p className="font-serif text-2xl italic text-ink">O prazo para confirmação de presença terminou em 03/12/2026.</p>
              <p className="mt-4 font-sans text-sm font-light leading-loose text-muted">
                Se ainda for possível confirmar sua presença, fale com a nossa assessoria pelo WhatsApp{" "}
                <a href={ASSESSORIA.whatsappUrl} target="_blank" rel="noopener noreferrer" className="text-sage-dark underline underline-offset-2" data-testid="rsvp-closed-whatsapp">
                  {ASSESSORIA.numeroLabel}
                </a>
                .
              </p>
            </div>
          </Reveal>
        ) : sent ? (
          <Reveal>
            <div className="paper-card gold-frame p-10 text-center" data-testid="rsvp-success">
              <CheckCircle2 size={44} className="mx-auto text-sage" />
              <h2 className="mt-5 font-script text-5xl text-ink">
                {sent.status === "confirmado" ? "Presença confirmada!" : "Ausência registrada"}
              </h2>
              {sent.status === "confirmado" ? (
                <div className="mt-5 text-left">
                  <p className="text-center font-sans text-sm font-light text-ink/80">
                    Recebemos a confirmação do convite de <strong>{sent.guestName}</strong>:
                  </p>
                  <ul className="mx-auto mt-4 max-w-sm space-y-1.5">
                    {sent.presentes.map((p, i) => (
                      <li key={i} className="flex items-center gap-2 font-sans text-sm font-light text-ink/80" data-testid={`rsvp-success-person-${i}`}>
                        <Check size={13} className="shrink-0 text-sage-dark" />
                        <span>
                          {p.name} <span className="text-muted">— {RSVP.categories[p.categoria].label}</span>
                        </span>
                      </li>
                    ))}
                  </ul>
                  <p className="mt-5 text-center font-sans text-sm font-light text-muted">Mal podemos esperar para celebrar com você. ❤️</p>
                </div>
              ) : (
                <p className="mt-4 font-sans text-sm font-light leading-loose text-ink/80">
                  Registramos que o convite de <strong>{sent.guestName}</strong> não comparecerá. Sentiremos sua falta e agradecemos o carinho de avisar.
                </p>
              )}
              <button data-testid="rsvp-again-btn" onClick={reset} className="btn-outline mt-8">
                Fazer outra confirmação
              </button>
            </div>
          </Reveal>
        ) : !invite ? (
          <Reveal>
            <div className="paper-card gold-frame p-8 sm:p-10" data-testid="rsvp-search-card">
              <label htmlFor="rsvp-search" className="eyebrow mb-2 block">Digite seu nome</label>
              <div className="relative">
                <Search size={16} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-muted" />
                <input
                  id="rsvp-search"
                  data-testid="rsvp-search-input"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Comece a digitar e selecione seu nome na lista"
                  autoComplete="off"
                  className="w-full border border-gold-border bg-white py-3.5 pl-11 pr-4 font-sans text-sm outline-none transition-colors focus:border-sage"
                />
              </div>
              {query.trim().length >= 2 && (
                <div className="mt-2 border border-gold-border bg-white" data-testid="rsvp-suggestions">
                  {searching ? (
                    <p className="px-4 py-3 font-sans text-sm font-light italic text-muted">Buscando...</p>
                  ) : suggestions.length === 0 ? (
                    <p className="px-4 py-3 font-sans text-sm font-light italic text-muted" data-testid="rsvp-no-results">
                      Nenhum nome encontrado na lista de convidados.
                    </p>
                  ) : (
                    <ul>
                      {suggestions.map((g) => (
                        <li key={g.id}>
                          <button
                            type="button"
                            data-testid={`rsvp-suggestion-${g.id}`}
                            onClick={() => selectInvite(g)}
                            className="w-full border-b border-gold-border/50 px-4 py-3 text-left font-serif text-lg text-ink transition-colors last:border-b-0 hover:bg-sage-light"
                          >
                            {g.name}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
              <p className="mt-5 font-sans text-xs font-light leading-relaxed text-muted">
                Já confirmou antes? Pesquise seu nome novamente para revisar ou atualizar sua resposta.
              </p>
            </div>
          </Reveal>
        ) : (
          <Reveal>
            <div className="paper-card gold-frame p-8 sm:p-10" data-testid="rsvp-form">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="eyebrow">Seu convite</p>
                  <h2 className="mt-2 font-script text-4xl text-ink" data-testid="rsvp-invite-name">{invite.name}</h2>
                </div>
                <button type="button" onClick={() => { setInvite(null); setExisting(null); }} data-testid="rsvp-back-btn" className="inline-flex items-center gap-1.5 font-sans text-[10px] uppercase tracking-[0.2em] text-muted transition-colors hover:text-sage-dark">
                  <Undo2 size={12} /> Trocar
                </button>
              </div>

              {existing && (
                <p className="mt-4 border border-gold bg-paper px-4 py-2.5 font-sans text-xs text-ink/70" data-testid="rsvp-existing-note">
                  Este convite já possui uma resposta registrada. Você pode revisar e atualizar abaixo.
                </p>
              )}

              <div className="mt-7 border-t border-gold-border pt-6">
                <p className="eyebrow mb-3">Você comparecerá?</p>
                <div className="flex flex-col gap-3 sm:flex-row">
                  <button
                    type="button"
                    data-testid="rsvp-titular-sim"
                    onClick={() => setTitularVai(true)}
                    className={`flex-1 border px-4 py-3 font-sans text-[11px] uppercase tracking-[0.2em] transition-colors ${titularVai ? "border-ink bg-ink text-paper" : "border-gold-border bg-white text-ink hover:bg-sage-light"}`}
                  >
                    Sim, estarei presente
                  </button>
                  <button
                    type="button"
                    data-testid="rsvp-titular-nao"
                    onClick={() => setTitularVai(false)}
                    className={`flex-1 border px-4 py-3 font-sans text-[11px] uppercase tracking-[0.2em] transition-colors ${!titularVai ? "border-ink bg-ink text-paper" : "border-gold-border bg-white text-ink hover:bg-sage-light"}`}
                  >
                    Não poderei ir
                  </button>
                </div>
              </div>

              {titularVai && (
                <div className="mt-6 space-y-3" data-testid="rsvp-companions">
                  <p className="font-sans text-[11px] uppercase tracking-[0.22em] text-sage-dark">
                    Quem vai com você?
                  </p>
                  {companions.map((c) => (
                    <div key={c.key} className="border border-gold-border bg-white px-4 py-3.5" data-testid={`rsvp-slot-${c.key}`}>
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                        <input
                          value={c.name}
                          onChange={(e) => updateSlot(c.key, { name: e.target.value })}
                          placeholder="Nome completo do acompanhante"
                          data-testid={`rsvp-slot-name-${c.key}`}
                          className="min-w-0 flex-1 border border-gold-border bg-white px-3.5 py-2.5 font-sans text-sm outline-none transition-colors focus:border-sage"
                        />
                        <select
                          value={c.categoria}
                          onChange={(e) => updateSlot(c.key, { categoria: e.target.value })}
                          data-testid={`rsvp-slot-cat-${c.key}`}
                          aria-label="Categoria etária"
                          className="border border-gold-border bg-white px-3.5 py-2.5 font-sans text-sm outline-none transition-colors focus:border-sage"
                        >
                          <option value="adulto">Adulto</option>
                          <option value="crianca">Criança</option>
                          <option value="bebe">Bebê</option>
                        </select>
                        <button
                          type="button"
                          onClick={() => removeSlot(c.key)}
                          data-testid={`rsvp-slot-remove-${c.key}`}
                          aria-label="Remover acompanhante"
                          className="grid h-9 w-9 shrink-0 place-items-center self-end border border-gold-border text-red-800 transition-colors hover:bg-red-50 sm:self-center"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={addCompanion}
                    disabled={companions.length >= MAX_COMPANIONS}
                    data-testid="rsvp-add-companion"
                    className="btn-outline w-full"
                  >
                    <Plus size={13} /> Adicionar acompanhante
                  </button>
                </div>
              )}

              <div className="mt-6 border-t border-gold-border pt-6">
                <label htmlFor="rsvp-phone" className="eyebrow mb-2 block">Qual telefone podemos utilizar para contato?</label>
                <input
                  id="rsvp-phone"
                  data-testid="rsvp-input-telefone"
                  value={phone}
                  onChange={(e) => setPhone(maskPhone(e.target.value))}
                  inputMode="tel"
                  autoComplete="tel"
                  required
                  placeholder="(41) 99999-0000"
                  className="w-full border border-gold-border bg-white px-4 py-3 font-sans text-sm outline-none transition-colors focus:border-sage"
                />
                <p className="mt-2 font-sans text-xs font-light leading-relaxed text-muted">
                  Informe um número de telefone válido, seu ou de um dos seus acompanhantes, para que a cerimonial possa entrar em contato caso seja necessário.
                </p>
                <input
                  value={phoneOwner}
                  onChange={(e) => setPhoneOwner(e.target.value)}
                  maxLength={120}
                  data-testid="rsvp-input-telefone-pessoa"
                  placeholder="De quem é este número? (opcional)"
                  className="mt-3 w-full border border-gold-border bg-white px-4 py-3 font-sans text-sm outline-none transition-colors focus:border-sage"
                />
              </div>

              <div className="mt-6">
                <label htmlFor="rsvp-msg" className="eyebrow mb-2 block">Mensagem para os noivos (opcional)</label>
                <textarea
                  id="rsvp-msg"
                  data-testid="rsvp-input-mensagem"
                  value={mensagem}
                  onChange={(e) => setMensagem(e.target.value)}
                  maxLength={600}
                  rows={3}
                  placeholder="Deixe um recado de carinho"
                  className="w-full resize-none border border-gold-border bg-white px-4 py-3 font-sans text-sm outline-none transition-colors focus:border-sage"
                />
              </div>

              <button
                type="button"
                onClick={submit}
                disabled={busy}
                data-testid="rsvp-submit-btn"
                className="btn-gold mt-6 w-full"
              >
                {busy ? "Enviando..." : titularVai ? "Confirmar presença" : "Registrar ausência"}
              </button>

              <p className="mt-5 flex flex-wrap items-center justify-center gap-1.5 text-center font-sans text-xs font-light text-muted">
                <MessageCircle size={12} />
                Prefere confirmar pelo WhatsApp da assessoria?
                <a href={ASSESSORIA.whatsappUrl} target="_blank" rel="noopener noreferrer" data-testid="rsvp-whatsapp-link" className="text-sage-dark underline underline-offset-2">
                  {ASSESSORIA.numeroLabel}
                </a>
                — até {ASSESSORIA.prazo}
              </p>
            </div>
          </Reveal>
        )}
      </section>
      <Toaster position="top-center" richColors />
    </div>
  );
}
